import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { ArrowDownTrayIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import AnimatedModal from '../ui/AnimatedModal';
import {
  FILLED_SCOPES,
  IncompleteDownloadError,
  buildGoColorsPayrollEntry,
  filledRangeFor,
  istToday,
  missingSummary,
  recordPayrollDownload,
  safeFileTitle,
  saveGoColorsExcel,
  saveGoColorsPayrollZipParts,
} from '../../pages/screenings/goColorsPayrollExport';

/**
 * Go Colors "Download", in two places:
 *   job page — `jobId` (+ `job`): every screening of that job post;
 *   job list — `storeFormat` ('EBO' | 'LFS'): every job post of that format, active and closed.
 * Options:
 *   Filled  — opens a modal: candidates not downloaded yet, or those whose ARM filled DOJ and
 *             gross salary in the last 7 days / this month / a custom range (downloaded or not).
 *             Documents ZIP + Excel; each candidate is then marked downloaded.
 *   Pending — the same Excel, no documents, for selected candidates still waiting on the ARM.
 * The server picks the candidates (GET /api/arm-salary/job/:jobId/payroll-export or
 * GET /api/arm-salary/payroll-export?storeFormat=).
 */
const MENU_OPTIONS = [
  {
    kind: 'filled',
    title: 'Filled',
    desc: 'Documents ZIP + Excel for candidates with salary details filled.',
  },
  {
    kind: 'pending',
    title: 'Pending',
    desc: 'Excel of candidates selected since 25 Sep whose ARM has not filled salary details yet.',
  },
];

const FILLED_CHOICES = [
  {
    scope: FILLED_SCOPES.NOT_DOWNLOADED,
    title: 'Not downloaded yet',
    desc: 'Selected since 25 Sep, salary filled, not downloaded before.',
  },
  { scope: FILLED_SCOPES.LAST_7_DAYS, title: 'Last 7 days', desc: 'Salary filled in the last 7 days, including today.' },
  { scope: FILLED_SCOPES.THIS_MONTH, title: 'This month', desc: 'Salary filled since the 1st of this month.' },
  { scope: FILLED_SCOPES.CUSTOM, title: 'Custom date range', desc: 'Salary filled between two dates.' },
];

const plural = (n) => `${n} candidate${n === 1 ? '' : 's'}`;

/** Where the candidates come from: one job post, or every job post of a store format. */
function exportSource({ jobId, job, storeFormat }) {
  if (storeFormat) {
    return {
      url: '/api/arm-salary/payroll-export',
      params: { storeFormat },
      across: `all ${storeFormat} job posts`,
      fileTitle: () => `${storeFormat}_All_Jobs`,
      fallbackMeta: { jobTitle: '', storeFormat },
    };
  }
  if (!jobId) return null;
  return {
    url: `/api/arm-salary/job/${jobId}/payroll-export`,
    params: {},
    across: 'every screening of this job post',
    fileTitle: (data) => safeFileTitle(data?.job?.title || job?.title),
    fallbackMeta: { jobTitle: job?.title, storeFormat: job?.storeFormat },
  };
}

export default function GoColorsPayrollDownloadMenu({ jobId, job, storeFormat }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(null);
  const [filledModalOpen, setFilledModalOpen] = useState(false);
  const [scope, setScope] = useState(FILLED_SCOPES.NOT_DOWNLOADED);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const containerRef = useRef(null);
  const today = istToday();
  const source = exportSource({ jobId, job, storeFormat });

  // Closing or reloading the tab mid-download loses the parts not saved yet (saved parts are
  // already marked downloaded, so a second "Not downloaded yet" carries on), so ask first.
  useEffect(() => {
    if (!busy) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [busy]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const run = async (kind, range) => {
    if (busy || !source) return;
    setBusy(kind);
    const toastId = toast.loading(`Collecting ${kind} candidates across ${source.across}...`);
    try {
      const params = range ? { ...source.params, kind, from: range.from, to: range.to } : { ...source.params, kind };
      const res = await axios.get(source.url, { params });
      const list = Array.isArray(res.data?.candidates) ? res.data.candidates : [];
      const skippedRecent = Array.isArray(res.data?.skippedRecent) ? res.data.skippedRecent : [];
      const skippedNote = skippedRecent.length
        ? `${plural(skippedRecent.length)} left out because their salary or a document came in during the last 5 minutes (${skippedRecent
            .map((s) => s.name)
            .join(', ')}). Download again in a few minutes to include them.`
        : '';
      if (!list.length) {
        toast.dismiss(toastId);
        let message = 'No pending candidates — every selected candidate has salary details filled.';
        if (kind === 'filled') {
          message = range
            ? `No candidates had salary details filled between ${range.from} and ${range.to}.`
            : 'No new filled candidates — everyone selected since 25 Sep with salary details filled has already been downloaded.';
        }
        toast(skippedNote || message, { duration: skippedNote ? 10000 : 4000 });
        return;
      }

      // Each candidate's own job post gives the Excel's Designation (the job list mixes jobs).
      const fallback = {
        jobTitle: res.data?.job?.title || source.fallbackMeta.jobTitle,
        storeFormat: res.data?.job?.storeFormat || source.fallbackMeta.storeFormat,
      };
      const entries = list.map((c) =>
        buildGoColorsPayrollEntry(
          c,
          c.documents,
          c.salary,
          { jobTitle: c.jobTitle || fallback.jobTitle, storeFormat: c.storeFormat || fallback.storeFormat },
          c.documentCounts
        )
      );
      const safeTitle = source.fileTitle(res.data);

      if (kind === 'pending') {
        saveGoColorsExcel({ entries, excelName: `GoColors_Pending_${safeTitle}_${today}.xlsx` });
        toast.dismiss(toastId);
        toast.success(`Pending Excel downloaded (${plural(entries.length)})`);
        return;
      }

      const fileBase = range
        ? `GoColors_Filled_${safeTitle}_${range.from}_to_${range.to}`
        : `GoColors_Filled_${safeTitle}_${today}`;
      let recordFailed = false;
      const { parts, incomplete } = await saveGoColorsPayrollZipParts({
        entries,
        zipBase: fileBase,
        excelBase: fileBase,
        onProgress: ({ part, parts: partCount, done, total }) =>
          toast.loading(
            partCount > 1
              ? `Downloading documents (ZIP ${part} of ${partCount}): ${done}/${total} candidates.\nIf Chrome asks to download multiple files, click Allow. Keep this tab open.`
              : `Downloading documents: ${done}/${total} candidates`,
            { id: toastId }
          ),
        onPartSaved: async (completeEntries, partIncomplete) => {
          try {
            await recordPayrollDownload(completeEntries.map((e) => e.candidateId), true, false, partIncomplete);
          } catch (err) {
            recordFailed = true;
            // eslint-disable-next-line no-console
            console.error('Failed to record payroll download:', err);
          }
        },
      });
      toast.dismiss(toastId);
      if (incomplete.length) {
        toast.error(
          `Downloaded ${plural(entries.length)}${parts > 1 ? ` in ${parts} ZIP files` : ''}, but documents are missing for ${plural(
            incomplete.length
          )}:\n${missingSummary(incomplete)}\nThey are not marked "Downloaded" — download them again.`,
          { duration: 15000 }
        );
      } else {
        toast.success(`Downloaded ${plural(entries.length)} with all their documents${parts > 1 ? ` in ${parts} ZIP files` : ''}`);
      }
      if (skippedNote) toast(skippedNote, { duration: 10000 });
      if (recordFailed) {
        toast.error(
          'Downloaded, but the "Downloaded" status could not be saved for some candidates. They will be included in "Not downloaded yet" next time.'
        );
      }
    } catch (err) {
      toast.dismiss(toastId);
      if (err instanceof IncompleteDownloadError) {
        recordPayrollDownload([], true, false, err.incomplete).catch(() => {});
        toast.error(
          `Download stopped, nothing more was saved. Documents could not be downloaded for:\n${missingSummary(err.incomplete)}\nCheck your internet connection and try again.`,
          { duration: 15000 }
        );
      } else {
        toast.error(err.response?.data?.message || 'Download failed. Please try again.');
      }
    } finally {
      setBusy(null);
    }
  };

  const pickMenuOption = (kind) => {
    setOpen(false);
    if (kind === 'filled') {
      setFilledModalOpen(true);
      return;
    }
    run('pending', null);
  };

  const startFilledDownload = () => {
    const range = filledRangeFor(scope, { today, from: customFrom, to: customTo });
    if (range?.error) {
      toast.error(range.error);
      return;
    }
    setFilledModalOpen(false);
    run('filled', range);
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        data-testid="gocolors-payroll-download-btn"
        onClick={() => setOpen((v) => !v)}
        disabled={!!busy}
        aria-haspopup="menu"
        aria-expanded={open}
        className="px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:border-[#007BFF]/50 hover:text-[#007BFF] hover:bg-[#007BFF]/5 flex items-center gap-2 font-semibold transition-all duration-200 text-sm disabled:opacity-60 disabled:cursor-wait"
      >
        <ArrowDownTrayIcon className="h-5 w-5" />
        <span>{busy ? 'Preparing…' : 'Download'}</span>
        <ChevronDownIcon className="h-4 w-4" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-2 w-[19rem] overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg"
        >
          {MENU_OPTIONS.map((o) => (
            <button
              key={o.kind}
              type="button"
              role="menuitem"
              data-testid={`gocolors-payroll-download-${o.kind}`}
              onClick={() => pickMenuOption(o.kind)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b last:border-b-0 border-gray-100"
            >
              <div className="text-sm font-semibold text-gray-900">{o.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{o.desc}</div>
            </button>
          ))}
        </div>
      )}

      <AnimatedModal
        isOpen={filledModalOpen}
        onClose={() => setFilledModalOpen(false)}
        ariaLabelledBy="gocolors-filled-download-title"
        contentClassName="w-full max-w-md mx-auto bg-white rounded-2xl shadow-2xl border border-gray-100"
      >
        <div data-testid="gocolors-filled-download-modal">
          <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
            <div>
              <h3 id="gocolors-filled-download-title" className="text-base font-semibold text-gray-900">
                Download filled candidates
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Documents ZIP + Excel across {source?.across}.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setFilledModalOpen(false)}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <fieldset className="px-6 py-4 space-y-2">
            <legend className="sr-only">Which candidates</legend>
            {FILLED_CHOICES.map((c) => (
              <label
                key={c.scope}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                  scope === c.scope ? 'border-[#007BFF] bg-[#007BFF]/5' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="gocolors-filled-scope"
                  value={c.scope}
                  checked={scope === c.scope}
                  onChange={() => setScope(c.scope)}
                  data-testid={`gocolors-filled-scope-${c.scope}`}
                  className="mt-0.5 text-[#007BFF] focus:ring-[#007BFF]"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">{c.title}</span>
                  <span className="block text-xs text-gray-500">{c.desc}</span>
                </span>
              </label>
            ))}

            {scope === FILLED_SCOPES.CUSTOM && (
              <div className="grid grid-cols-2 gap-3 pt-2">
                <label className="text-xs font-medium text-gray-700">
                  From
                  <input
                    type="date"
                    value={customFrom}
                    max={customTo || today}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    data-testid="gocolors-filled-from"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-[#007BFF] focus:ring-[#007BFF]"
                  />
                </label>
                <label className="text-xs font-medium text-gray-700">
                  To
                  <input
                    type="date"
                    value={customTo}
                    min={customFrom || undefined}
                    max={today}
                    onChange={(e) => setCustomTo(e.target.value)}
                    data-testid="gocolors-filled-to"
                    className="mt-1 block w-full rounded-lg border border-gray-300 px-2 py-1.5 text-sm focus:border-[#007BFF] focus:ring-[#007BFF]"
                  />
                </label>
              </div>
            )}

            {scope !== FILLED_SCOPES.NOT_DOWNLOADED && (
              <p className="text-xs text-gray-500 pt-1">
                Dates are when the ARM filled the salary details. Candidates downloaded before are included again.
              </p>
            )}
          </fieldset>

          <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
            <button
              type="button"
              onClick={() => setFilledModalOpen(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={startFilledDownload}
              disabled={!!busy}
              data-testid="gocolors-filled-download-submit"
              className="px-4 py-2 text-sm font-medium text-white bg-[#007BFF] rounded-lg hover:bg-[#0062CC] disabled:opacity-60 inline-flex items-center gap-2"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              Download
            </button>
          </div>
        </div>
      </AnimatedModal>
    </div>
  );
}
